import { WTooltipButton } from "@/components/button/WToolButton.tsx";
import { SimpleSwitch } from "@/components/dev/SimpleSwitch.tsx";
import { WSimpleSelect } from "@/components/dev/WSimpleSelect.tsx";
import { billingVariants, type MockDbOptions, mockDbVariants, pricingVariants } from "@/mock-api/mockDbVariants.ts";
import { getMockDbOptions, resetMockDb } from "@/mock-api/msw/mswInit.ts";
import { queryClientAtom } from "@/modules/api/queryClient.ts";
import { clearAllToasts } from "@/modules/notifications/toast.tsx";
import { atom, useAtom, useSetAtom } from "jotai";

export default function MockConfig() {
    return (
        <div className="flex flex-col gap-0.5rem">
            <div className="flex flex-row gap-0.5rem">
                <ResetMockDbButton />
                <div className="grow">
                    <MockDbSelect />
                </div>
                <SingleLocationChoice />
            </div>
            <div className="flex flex-row gap-0.5rem children:grow">
                <MockPricingSelect />
                <MockBillingSelect />
            </div>
        </div>
    );
}

// Cache the last set value since we do not have a subscription to receive options updates from the mock DB
const cachedOptionsAtom = atom<MockDbOptions | undefined>(undefined);

const mockDbOptionsAtom = atom(
    async (get) => {
        console.debug("mockDbOptionsAtom get");
        return get(cachedOptionsAtom) ?? (await getMockDbOptions());
    },
    async (get, set, options: MockDbOptions) => {
        console.debug("mockDbOptionsAtom set", options);
        await resetMockDb(options);
        set(cachedOptionsAtom, options);

        // NB: Invalidate all client caches (since the mock DB was re-built)
        clearAllToasts();
        // TODO: should also reset all error boundaries
        await get(queryClientAtom).resetQueries({ type: "all" });
    },
);

function ResetMockDbButton() {
    const setOptions = useSetAtom(mockDbOptionsAtom);

    return (
        <WTooltipButton
            variant="ghost"
            icon="i-lucide:trash"
            action={async () => await setOptions({})}
            title="Reset mock DB"
        />
    );
}

function MockDbSelect() {
    return <MockVariantSelect title="DB variant" variants={mockDbVariants} field="variant" />;
}

function MockPricingSelect() {
    return <MockVariantSelect title="Pricing variant" variants={pricingVariants} field="pricingVariant" />;
}

function MockBillingSelect() {
    return <MockVariantSelect title="Billing variant" variants={billingVariants} field="billingVariant" />;
}

function MockVariantSelect({
    title,
    variants,
    field,
}: {
    title: string;
    variants: Record<string, string>;
    field: "variant" | "pricingVariant" | "billingVariant" | "hostPools";
}) {
    const [options, setOptions] = useAtom(mockDbOptionsAtom);

    const currentValue = options[field];

    const items = Object.entries(variants).map(([value, label]) => ({ value, label }));

    return (
        <WSimpleSelect
            placeholder={title}
            value={currentValue ?? null}
            items={items}
            onChange={async (value) => {
                await setOptions({ ...options, [field]: value });
            }}
        />
    );
}

function SingleLocationChoice() {
    const [options, setOptions] = useAtom(mockDbOptionsAtom);

    return (
        <SimpleSwitch
            value={options.singleLocation}
            onChange={async (singleLocation) => await setOptions({ ...options, singleLocation })}
        >
            Single
            <br />
            location
        </SimpleSwitch>
    );
}
